import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, ExternalLink, Save, Upload } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { parseEventDate } from "@/lib/events";
import { AdminSeo } from "@/pages/AdminSeo";

const isPast = (date) => {
  const when = parseEventDate(date);
  if (!when) return false;
  const now = new Date();
  return when < new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const TABS = [
  { id: "brand", label: "Brand & Text" },
  { id: "contact", label: "Contact Info" },
  { id: "staff", label: "Staff" },
  { id: "events", label: "Events" },
  { id: "closures", label: "Office Closures" },
  { id: "seo", label: "SEO & Scripts" },
];

const inputCls =
  "w-full border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:border-brand transition-colors duration-300 bg-white";

function Field({ label, children }) {
  return (
    <label className="block mb-5">
      <span className="text-[11px] tracking-[0.2em] uppercase font-bold text-slate-500 block mb-2">{label}</span>
      {children}
    </label>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [auth, setAuth] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [draft, setDraft] = useState(null);
  const [tab, setTab] = useState("brand");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (location.state?.user) return;
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        setAuth(true);
      })
      .catch(() => {
        setAuth(false);
        navigate("/admin/login", { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (auth === true) {
      api.get("/content").then((r) => setDraft(r.data));
    }
  }, [auth]);

  const set = (key, value) => setDraft((d) => ({ ...d, [key]: value }));
  const setContact = (key, value) => setDraft((d) => ({ ...d, contact: { ...d.contact, [key]: value } }));
  const setSeo = (key, value) => setDraft((d) => ({ ...d, seo: { ...d.seo, [key]: value } }));
  const setSeoPage = (page, key, value) =>
    setDraft((d) => ({ ...d, seo: { ...d.seo, pages: { ...d.seo.pages, [page]: { ...(d.seo.pages?.[page] || {}), [key]: value } } } }));
  const setRow = (list, i, key, value) =>
    setDraft((d) => ({ ...d, [list]: d[list].map((row, idx) => (idx === i ? { ...row, [key]: value } : row)) }));
  const addRow = (list, blank) => setDraft((d) => ({ ...d, [list]: [...d[list], blank] }));
  const removeRow = (list, i) => setDraft((d) => ({ ...d, [list]: d[list].filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", draft);
      toast.success("Saved — changes are live on the site");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("logo_url", data.url);
      toast.success("Logo uploaded — press Save to publish");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const uploadVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("hero_video", data.url);
      toast.success("Video uploaded — press Save to publish");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const logout = async () => {
    await api.post("/auth/logout");
    navigate("/admin/login", { replace: true });
  };

  if (auth === null || !draft) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50" data-testid="admin-loading">
        <p className="text-slate-400 text-sm tracking-[0.2em] uppercase font-bold">Loading…</p>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-navy text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <p className="font-heading text-lg uppercase tracking-wide" data-testid="admin-title">
            Local 675 — Site Admin
          </p>
          <div className="flex items-center gap-5">
            <span className="text-white/50 text-xs hidden sm:inline" data-testid="admin-user-email">{user?.email}</span>
            <Link to="/" data-testid="admin-view-site-link" className="inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.15em] uppercase text-white/70 hover:text-white transition-colors duration-300">
              <ExternalLink className="h-3.5 w-3.5" /> View site
            </Link>
            <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.15em] uppercase text-white/70 hover:text-white transition-colors duration-300">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-[220px_1fr] gap-10">
        <nav className="flex lg:flex-col gap-2 overflow-x-auto" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`text-left text-xs font-bold tracking-[0.15em] uppercase px-4 py-3 whitespace-nowrap transition-colors duration-300 ${
                tab === t.id ? "bg-brand text-white" : "bg-white text-slate-600 hover:bg-brand-light border border-slate-200"
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>

        <div className="bg-white border border-slate-200 p-6 sm:p-10">
          {tab === "brand" && (
            <div data-testid="admin-panel-brand">
              <h2 className="font-heading text-2xl uppercase text-slate-900 mb-8">Brand & Text</h2>
              <Field label="Logo">
                <div className="flex items-center gap-6 flex-wrap">
                  <img src={draft.logo_url} alt="Current logo" className="h-16 w-auto bg-navy border border-slate-200 px-2 py-1" data-testid="admin-logo-preview" />
                  <label className="inline-flex items-center gap-2 cursor-pointer bg-navy hover:bg-brand text-white text-xs font-bold tracking-[0.15em] uppercase px-5 py-3 transition-colors duration-300" data-testid="admin-logo-upload-button">
                    <Upload className="h-4 w-4" /> Upload new logo
                    <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="admin-logo-upload-input" />
                  </label>
                </div>
              </Field>
              <Field label="Logo URL (or paste an image link)">
                <input className={inputCls} value={draft.logo_url} onChange={(e) => set("logo_url", e.target.value)} data-testid="admin-logo-url-input" />
              </Field>
              <Field label="Logo backdrop (the logo is transparent — pick how it sits on dark sections)">
                <select className={inputCls} value={draft.logo_backdrop || "none"} onChange={(e) => set("logo_backdrop", e.target.value)} data-testid="admin-logo-backdrop-select">
                  <option value="none">Transparent — no background</option>
                  <option value="white">White card behind logo</option>
                </select>
              </Field>
              <Field label="Hero headline (new line = new masked line)">
                <textarea rows={2} className={inputCls} value={draft.hero_headline} onChange={(e) => set("hero_headline", e.target.value)} data-testid="admin-hero-headline-input" />
              </Field>
              <Field label="Hero subhead">
                <textarea rows={2} className={inputCls} value={draft.hero_subhead} onChange={(e) => set("hero_subhead", e.target.value)} data-testid="admin-hero-subhead-input" />
              </Field>
              <Field label="Hero background image URL">
                <input className={inputCls} value={draft.hero_image} onChange={(e) => set("hero_image", e.target.value)} data-testid="admin-hero-image-input" />
              </Field>
              <Field label="Hero background video (optional — replaces the photo with a looping video)">
                <div className="flex items-center gap-4 flex-wrap">
                  {draft.hero_video ? (
                    <video src={draft.hero_video} muted className="h-20 border border-slate-200" data-testid="admin-hero-video-preview" />
                  ) : (
                    <p className="text-sm text-slate-400">No video set — photo is used</p>
                  )}
                  <label className="inline-flex items-center gap-2 cursor-pointer bg-navy hover:bg-brand text-white text-xs font-bold tracking-[0.15em] uppercase px-5 py-3 transition-colors duration-300" data-testid="admin-hero-video-upload-button">
                    <Upload className="h-4 w-4" /> Upload video
                    <input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={uploadVideo} data-testid="admin-hero-video-upload-input" />
                  </label>
                  {draft.hero_video && (
                    <button type="button" onClick={() => set("hero_video", "")} data-testid="admin-hero-video-remove-button" className="text-xs font-bold tracking-[0.15em] uppercase text-red-600 hover:text-red-700 transition-colors duration-300">
                      Remove video
                    </button>
                  )}
                </div>
                <input className={`${inputCls} mt-3`} placeholder="…or paste a video URL" value={draft.hero_video || ""} onChange={(e) => set("hero_video", e.target.value)} data-testid="admin-hero-video-url-input" />
              </Field>
              <Field label="About paragraph">
                <textarea rows={4} className={inputCls} value={draft.about_text} onChange={(e) => set("about_text", e.target.value)} data-testid="admin-about-input" />
              </Field>
              <Field label="Footer text">
                <input className={inputCls} value={draft.footer_text} onChange={(e) => set("footer_text", e.target.value)} data-testid="admin-footer-input" />
              </Field>
            </div>
          )}

          {tab === "contact" && (
            <div data-testid="admin-panel-contact">
              <h2 className="font-heading text-2xl uppercase text-slate-900 mb-8">Contact Info</h2>
              <Field label="Email">
                <input className={inputCls} value={draft.contact.email} onChange={(e) => setContact("email", e.target.value)} data-testid="admin-contact-email-input" />
              </Field>
              <Field label="Main number">
                <input className={inputCls} value={draft.contact.phone} onChange={(e) => setContact("phone", e.target.value)} data-testid="admin-contact-phone-input" />
              </Field>
              <Field label="Address">
                <input className={inputCls} value={draft.contact.address} onChange={(e) => setContact("address", e.target.value)} data-testid="admin-contact-address-input" />
              </Field>
              <Field label="Office hours">
                <input className={inputCls} value={draft.contact.hours} onChange={(e) => setContact("hours", e.target.value)} data-testid="admin-contact-hours-input" />
              </Field>
            </div>
          )}

          {tab === "staff" && (
            <div data-testid="admin-panel-staff">
              <h2 className="font-heading text-2xl uppercase text-slate-900 mb-8">Staff Directory</h2>
              {draft.staff.map((person, i) => (
                <div key={i} className="border border-slate-200 p-5 mb-4 grid sm:grid-cols-2 gap-4 relative" data-testid={`admin-staff-row-${i}`}>
                  <input className={inputCls} placeholder="Role" value={person.role} onChange={(e) => setRow("staff", i, "role", e.target.value)} data-testid={`admin-staff-role-${i}`} />
                  <input className={inputCls} placeholder="Name" value={person.name} onChange={(e) => setRow("staff", i, "name", e.target.value)} data-testid={`admin-staff-name-${i}`} />
                  <input className={inputCls} placeholder="Phone" value={person.phone} onChange={(e) => setRow("staff", i, "phone", e.target.value)} data-testid={`admin-staff-phone-${i}`} />
                  <input className={inputCls} placeholder="Email" value={person.email} onChange={(e) => setRow("staff", i, "email", e.target.value)} data-testid={`admin-staff-email-${i}`} />
                  <button onClick={() => removeRow("staff", i)} data-testid={`admin-staff-remove-${i}`} className="absolute -top-2.5 -right-2.5 bg-white border border-slate-300 text-slate-400 hover:text-red-600 hover:border-red-300 p-1.5 transition-colors duration-300">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <button onClick={() => addRow("staff", { role: "", name: "", phone: "", email: "" })} data-testid="admin-staff-add-button" className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.15em] uppercase text-brand hover:text-brand-dark transition-colors duration-300 mt-2">
                <Plus className="h-4 w-4" /> Add staff member
              </button>
            </div>
          )}

          {tab === "events" && (
            <div data-testid="admin-panel-events">
              <h2 className="font-heading text-2xl uppercase text-slate-900 mb-8">Events</h2>
              <Field label="Events shown on the home page">
                <select className={inputCls} value={draft.home_events_count ?? 3} onChange={(e) => set("home_events_count", Number(e.target.value))} data-testid="admin-home-events-count-select">
                  {[1, 2, 3, 4, 5, 6, 8, 10].map((n) => (
                    <option key={n} value={n}>{n} {n === 1 ? "event" : "events"}</option>
                  ))}
                </select>
                <span className="block mt-2 text-xs text-slate-400">Only upcoming events are shown — past dates drop off automatically on the home and events pages.</span>
              </Field>
              {draft.events.map((event, i) => (
                <div key={i} className={`border p-5 mb-4 grid sm:grid-cols-2 gap-4 relative ${isPast(event.date) ? "border-amber-300 bg-amber-50/40" : "border-slate-200"}`} data-testid={`admin-event-row-${i}`}>
                  {isPast(event.date) && (
                    <span className="sm:col-span-2 text-[11px] tracking-[0.15em] uppercase font-bold text-amber-700" data-testid={`admin-event-past-${i}`}>
                      Past event — hidden on the site
                    </span>
                  )}
                  <input className={inputCls} placeholder="Title" value={event.title} onChange={(e) => setRow("events", i, "title", e.target.value)} data-testid={`admin-event-title-${i}`} />
                  <input className={inputCls} placeholder="Date (e.g. Monday, September 7, 2026)" value={event.date} onChange={(e) => setRow("events", i, "date", e.target.value)} data-testid={`admin-event-date-${i}`} />
                  <input className={inputCls} placeholder="Time (optional)" value={event.time} onChange={(e) => setRow("events", i, "time", e.target.value)} data-testid={`admin-event-time-${i}`} />
                  <input className={inputCls} placeholder="Location (optional)" value={event.location} onChange={(e) => setRow("events", i, "location", e.target.value)} data-testid={`admin-event-location-${i}`} />
                  <button onClick={() => removeRow("events", i)} data-testid={`admin-event-remove-${i}`} className="absolute -top-2.5 -right-2.5 bg-white border border-slate-300 text-slate-400 hover:text-red-600 hover:border-red-300 p-1.5 transition-colors duration-300">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <button onClick={() => addRow("events", { title: "", date: "", time: "", location: "" })} data-testid="admin-event-add-button" className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.15em] uppercase text-brand hover:text-brand-dark transition-colors duration-300 mt-2">
                <Plus className="h-4 w-4" /> Add event
              </button>
            </div>
          )}

          {tab === "closures" && (
            <div data-testid="admin-panel-closures">
              <h2 className="font-heading text-2xl uppercase text-slate-900 mb-8">Office Closures</h2>
              {draft.closures.map((closure, i) => (
                <div key={i} className="border border-slate-200 p-5 mb-4 grid sm:grid-cols-2 gap-4 relative" data-testid={`admin-closure-row-${i}`}>
                  <input className={inputCls} placeholder="Title" value={closure.title} onChange={(e) => setRow("closures", i, "title", e.target.value)} data-testid={`admin-closure-title-${i}`} />
                  <input className={inputCls} placeholder="Date" value={closure.date} onChange={(e) => setRow("closures", i, "date", e.target.value)} data-testid={`admin-closure-date-${i}`} />
                  <button onClick={() => removeRow("closures", i)} data-testid={`admin-closure-remove-${i}`} className="absolute -top-2.5 -right-2.5 bg-white border border-slate-300 text-slate-400 hover:text-red-600 hover:border-red-300 p-1.5 transition-colors duration-300">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <button onClick={() => addRow("closures", { title: "Office Closed", date: "" })} data-testid="admin-closure-add-button" className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.15em] uppercase text-brand hover:text-brand-dark transition-colors duration-300 mt-2">
                <Plus className="h-4 w-4" /> Add closure
              </button>
            </div>
          )}

          {tab === "seo" && <AdminSeo seo={draft.seo} setSeo={setSeo} setSeoPage={setSeoPage} />}

          <div className="mt-10 pt-6 border-t border-slate-200">
            <button
              onClick={save}
              disabled={saving}
              data-testid="admin-save-button"
              className="inline-flex items-center gap-2 bg-brand hover:bg-brand-dark disabled:opacity-60 text-white text-xs font-bold tracking-[0.2em] uppercase px-8 py-4 transition-colors duration-300"
            >
              <Save className="h-4 w-4" />
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}
